/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  EFormAction,
  EOnHandDeliveryStatus,
  ErrorMessages,
  FORM_STYLES,
  XL_GRID_PROPS,
} from "../../constants";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import {
  compareObjects,
  scrollToAndFocusFirstError,
  scrollToAndFocusField,
} from "../../helpers";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import get from "lodash/get";
import {
  intiOnHandFollowupFormValue,
  onHandFollowupSchema,
} from "../../schema/onHandFollowUp";
import NoteBox from "../../components/noteBox/NoteBox";
import { IOhFollowUp } from "../../models/onHandDelivery";
import { OnHandFollowUpAPI } from "../../API/OnHandFollowUpAPI";
import { hasCompleteOnHandPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { useCanViewByOwnershipPermission } from "../../@hooks/useCanViewByOwnershipPermission";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  osdType?: string;
  isEditMode?: boolean;
  isCleared?: boolean;
  onRefresh?: () => void;
  entryBy?: string;
  assignedTo?: string;
};

const OnHandFollowup: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  osdType,
  isEditMode = true,
  isCleared,
  onRefresh,
  entryBy,
  assignedTo,
}) => {
  const dispatch = useDispatch();
  const followupDetails = useMemo(
    () => osdDetails?.followup,
    [osdDetails?.followup],
  );
  const hasCompleteOnHandPermission = useSelector(
    hasCompleteOnHandPermissionSelector,
  );
  const canViewCompleteButton = useCanViewByOwnershipPermission({
    enabledFor: hasCompleteOnHandPermission,
    entryBy,
    assignedTo,
  });
  const [loading, setLoading] = useState(false);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString(),
  );
  const [status, setStatus] = useState(0);
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(isEditMode);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<any>(
    intiOnHandFollowupFormValue,
  );

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    setFieldError,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: onHandFollowupSchema,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (_values) => {},
  });

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  // Clear error immediately when field value changes
  const handleChangeWithClearError = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = e.target;
      if (errors[name]) {
        setFieldError(name, undefined);
      }
      handleChange(e);
    },
    [handleChange, errors, setFieldError],
  );

  // Handler for FormControlLabel (Radio buttons) - different event signature
  const handleRadioChangeWithClearError = useCallback(
    (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
      const target = e.target as HTMLInputElement;
      const { name } = target;
      if (errors[name]) {
        setFieldError(name, undefined);
      }
      handleChange(e as React.ChangeEvent<HTMLInputElement>);
    },
    [handleChange, errors, setFieldError],
  );

  const setFieldValueWithClearError = useCallback(
    (field: string, value: any, shouldValidate?: boolean) => {
      if (errors[field]) {
        setFieldError(field, undefined);
      }
      setFieldValue(field, value, shouldValidate);
    },
    [setFieldValue, errors, setFieldError],
  );

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined,
  ) => {
    setInitValues((prevState: any) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  // Follow-up action
  const normalizeYesNoFields = (params: IOhFollowUp) => {
    const yesNoFields: (keyof IOhFollowUp)[] = [
      "didTerminalClearException",
      "isF9NotFlashingInAde",
      "hasBolInstructions",
      "followedBolInstructions",
      "doF18NotesMatchActions",
      "hasImageCommunication",
      "wasEveryDeliveryAttemptMade",
      "wasShipperContactedWithin24hrs",
      "isFreightOnHandDueToDamage",
      "ohnVerbiageCorrect",
      "wasContactFrom7823",
      "wasContactFrom8511",
      "googleCheck",
      "pickupScreenCheck",
      "salesRepCheck",
      "isShipperValueInWriting",
      "communicationImagedByTerminal",
      "f18NotesUpdatedByAll",
      "freightHasAppointment",
      "storageChargesApproved",
    ];

    yesNoFields.forEach((field) => {
      const value = params[field];
      params[field] = value === "Y" || value === "N" ? value : undefined;
    });
  };

  const pruneConditionalFields = (params: IOhFollowUp) => {
    const conditionalDeletes: Partial<
      Record<keyof IOhFollowUp, (keyof IOhFollowUp)[]>
    > = {
      isFreightOnHandDueToDamage: [
        "shipmentCallFor",
        "productDescription",
        "packageDamageDescription",
        "actualProductDamageDetails",
        "actualProductNotDamagedDetails",
        "canProductBeRecoupedForRedelivery",
        "isProductRepairable",
        "hasValidDamagePictures",
        "isProductGlass",
      ],
      pickupScreenCheck: ["pickupScreenResponse"],
      wasContactFrom8511: ["stepsTakenFrom8511"],
      salesRepCheck: ["salesRepResponse"],
      googleCheck: ["googleCheckResponse"],
      hasBolInstructions: [
        "followedBolInstructions",
        "doF18NotesMatchActions",
        "hasImageCommunication",
      ],
      wasContactFrom7823: ["stepsTakenFrom7823"],
    };

    Object.entries(conditionalDeletes).forEach(([field, keys]) => {
      if (params[field as keyof IOhFollowUp] !== "Y") {
        keys.forEach((k) => delete params[k]);
      }
    });
  };

  const handleApiSuccess = (
    statusNumber: number,
    action: EFormAction,
    dispatch: any,
  ) => {
    setStatus(statusNumber);
    setIsCompleting(false);
    setInvestigationStatus(statusNumber);
    setFormFieldsDisabled(true);

    let message = ErrorMessages.followUpSave;
    if (action === EFormAction.complete) {
      message = ErrorMessages.followUpCompleted;
    }
    if (action === EFormAction.submit) {
      message = ErrorMessages.followUpSubmitted;
    }
    if (
      action === EFormAction.save &&
      statusNumber === EOnHandDeliveryStatus.srpendingpproval
    )
      message = ErrorMessages.followUpUpdate;

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        message,
        Date.now().toString(),
      ),
    );

    // Refresh data after successful action - this will update form values via useEffect
    onRefresh?.();
  };

  const handleApiError = (err: unknown, dispatch: any) => {
    const errorMessage =
      typeof err === "string"
        ? err
        : "Exception Investigation failed. Please try again.";

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        errorMessage,
        Date.now().toString(),
      ),
    );
    console.error("Exception Investigation error:", err);
  };

  const saveOrComplete = useCallback(
    async (action = EFormAction.save) => {
      const params: IOhFollowUp = { ...values };

      normalizeYesNoFields(params);
      pruneConditionalFields(params);

      const errors = await validateForm();
      if (
        (action !== EFormAction.save ||
          status === EOnHandDeliveryStatus.srpendingpproval) &&
        Object.keys(errors).length > 0
      ) {
        setIsCompleting(true);
        scrollToAndFocusFirstError(errors);
        return;
      }

      setLoading(true);

      try {
        const { statusNumber } = await OnHandFollowUpAPI(
          params,
          osdNumber,
          action,
        );
        handleApiSuccess(statusNumber, action, dispatch);
      } catch (err) {
        handleApiError(err, dispatch);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, dispatch, validateForm, osdNumber, osdType],
  );

  const resetToLastSaved = () => {
    resetForm({ values: initValues }); // This resets all values and validations correctly
    setResetTimestamps(Date.now().toString());
    scrollToAndFocusField("billDetailsDescription");
  };

  const patchFormFieldValues = (formValues: IOhFollowUp) => {
    Object.entries(intiOnHandFollowupFormValue).forEach(([key]) => {
      const typedKey = key as keyof IOhFollowUp;
      const value = formValues[typedKey] ?? undefined;

      setFieldValue(typedKey, value, Boolean(value));
      updateInitValue(typedKey, value);
    });
  };

  const renderFormErrorMessage = (
    filedName: string,
    className = "form-error",
  ) => {
    if (errors[filedName] && isCompleting)
      return <span className={className}>{errors[filedName] as string}</span>;
    return null;
  };

  const hasErrors = (fieldKey: string): boolean => {
    return Boolean(get(errors, `${fieldKey}`, "") && isCompleting);
  };

  const tolltip = (key: string, text: string) => {
    return <TooltipWithClickAway tooltipText={text} tooltipKey={key} />;
  };

  type InputFieldOptions = {
    fieldName: string;
    label?: string;
    placeholder?: string;
    strike?: string;
    md?: number;
    maxLength?: number;
    tooltipText?: string;
    tooltipKey?: string;
    disabled?: boolean;
    rows?: number;
  };

  const renderInputFeild = (options: InputFieldOptions) => {
    const {
      fieldName,
      label = "",
      placeholder = "",
      strike = "",
      md = 12,
      maxLength = 150,
      tooltipText = "",
      tooltipKey = "",
      disabled = formFieldsDisabled,
      rows = 1,
    } = options;

    return (
      <Grid item md={md} xs={12}>
        <FormControl fullWidth size="small">
          {label && (
            <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
              {label} <sup>{strike}</sup>
              &nbsp;
              {tooltipText && tooltipText !== "" && (
                <span className="cus-tooltip label-tooltip">
                  {tolltip(tooltipKey, tooltipText)}
                </span>
              )}
            </InputLabel>
          )}
          <TextField
            autoComplete="off"
            id={fieldName}
            placeholder={placeholder}
            variant="outlined"
            name={fieldName}
            rows={rows}
            data-testid={fieldName}
            multiline={rows > 1}
            value={values[fieldName] ?? ""}
            onChange={handleChangeWithClearError}
            error={hasErrors(fieldName)}
            disabled={disabled || formFieldsDisabled}
            inputProps={{ maxLength: maxLength }}
            onBlur={handleBlur}
          />
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderRadioGroup = (
    fieldName: string,
    label: string = "",
    nTooltipKey: string = "",
    nTooltipText: string = "",
    yTooltipKey: string = "",
    yTooltipText: string = "",
  ) => {
    return (
      <Grid item {...XL_GRID_PROPS} className="pt-0">
        <FormControl className="radio-group question-group">
          <FormLabel>
            {label} <sup>*</sup>
          </FormLabel>
          <RadioGroup row name={fieldName}>
            <FormControlLabel
              value="Y"
              control={<Radio disabled={formFieldsDisabled} />}
              label="Yes"
              name={fieldName}
              checked={values[fieldName] === "Y"}
              onChange={handleRadioChangeWithClearError}
              onBlur={handleBlur}
            />
            {values[fieldName] === "Y" && yTooltipText && (
              <div className="tooltip-custom">
                {tolltip(yTooltipKey, yTooltipText)}
              </div>
            )}
            <FormControlLabel
              value="N"
              control={<Radio disabled={formFieldsDisabled} />}
              label="No"
              name={fieldName}
              checked={values[fieldName] === "N"}
              onChange={handleRadioChangeWithClearError}
              onBlur={handleBlur}
              className={
                yTooltipText && values[fieldName] === "Y"
                  ? "margin-left-20"
                  : ""
              }
            />
            {values[fieldName] === "N" && nTooltipText && (
              <div className="tooltip-custom">
                {tolltip(nTooltipKey, nTooltipText)}
              </div>
            )}
          </RadioGroup>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderCheckBox = (fieldName: string = "", label: string = "") => {
    return (
      <Grid item {...XL_GRID_PROPS} className="filter-margin">
        <FormControl className="radio-group question-group">
          <FormLabel>
            <FormControlLabel
              className="checkbox-shoartage"
              control={
                <Checkbox
                  disableRipple
                  checked={values[fieldName] === "Y"}
                  disabled={formFieldsDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValueWithClearError(
                      fieldName,
                      e.target.checked ? "Y" : "N",
                      true,
                    );
                  }}
                  name={fieldName}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
              label=""
            />
            {label} <sup>*</sup>
          </FormLabel>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderNotes = (message: string) => {
    return (
      <Grid item {...XL_GRID_PROPS} className="filter-margin">
        <NoteBox data-testid={message} message={`Note: ${message}`} />
      </Grid>
    );
  };

  const isEditable = status !== EOnHandDeliveryStatus.needsCleared;
  const isFollowupInProgress =
    status === EOnHandDeliveryStatus.followupInProgress;
  const isSrPendingApproval = status === EOnHandDeliveryStatus.srpendingpproval;
  const hasFormChanges = !compareObjects(initValues, values);

  // Render reusable button components
  const renderEditButton = () => (
    <Button
      variant="contained"
      onClick={enableEditing}
      className="assign ml mr-0"
      data-testid="edit-button"
    >
      Edit
    </Button>
  );

  const renderCancelButton = () => (
    <Button
      variant="contained"
      onClick={resetToLastSaved}
      className="clear-btn mr-0 ml"
      disabled={loading}
      data-testid={!followupDetails ? "clear-button" : "cancel-button"}
    >
      {!followupDetails ? "Clear" : "Cancel"}
    </Button>
  );

  const renderCompleteButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.complete)}
      className="entry ml"
      type="submit"
      data-testid="complete-button"
    >
      Complete
    </Button>
  );

  const renderUpdateButton = () => (
    <Button
      variant="contained"
      onClick={
        hasFormChanges ? () => saveOrComplete(EFormAction.save) : undefined
      }
      className={`assign ml ${!hasFormChanges ? "disable disable-btn" : ""}`}
      type="submit"
      data-testid="update-button"
    >
      Update
    </Button>
  );

  const renderSaveButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.save)}
      className="entry ml"
      type="submit"
      data-testid="save-button"
    >
      Save
    </Button>
  );

  const renderSubmitButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.submit)}
      className="entry ml"
      type="submit"
      data-testid="submit-button"
    >
      Submit
    </Button>
  );

  const actionButtons = () => {
    // Hide all action buttons if not editable or user lacks permission for srpendingpproval status
    if (!isEditable || (!canViewCompleteButton && isSrPendingApproval)) {
      return null;
    }

    // SR Pending Approval with permission: Edit/Complete or Cancel/Update
    if (canViewCompleteButton && isSrPendingApproval) {
      return (
        <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
          {formFieldsDisabled ? (
            <>
              {renderEditButton()}
              {renderCompleteButton()}
            </>
          ) : (
            <>
              {renderCancelButton()}
              {renderUpdateButton()}
            </>
          )}
        </Grid>
      );
    }

    // Follow-up In Progress: Edit or Cancel/Save/Submit
    if (isFollowupInProgress) {
      return (
        <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
          {formFieldsDisabled ? (
            renderEditButton()
          ) : (
            <>
              {renderCancelButton()}
              {renderSaveButton()}
              {renderSubmitButton()}
            </>
          )}
        </Grid>
      );
    }

    // Default: just Edit button (for other editable statuses)
    return (
      <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
        {formFieldsDisabled ? renderEditButton() : renderCancelButton()}
      </Grid>
    );
  };

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (!followupDetails) return;
    patchFormFieldValues(followupDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupDetails]);

  useEffect(() => {
    if (osdDetails?.investigationStatusNumber)
      setStatus(osdDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdDetails?.investigationStatusNumber]);

  // Enable form directly when no followupDetails and status is followupInProgress
  useEffect(() => {
    if (
      !followupDetails &&
      status === EOnHandDeliveryStatus.followupInProgress
    ) {
      setFormFieldsDisabled(false);
    } else if (
      followupDetails &&
      status === EOnHandDeliveryStatus.followupInProgress
    ) {
      setFormFieldsDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupDetails, status]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <>
            {renderInputFeild({
              fieldName: "billDetailsDescription",
              label: "Provide details regarding the bill, specifically the item quantities and descriptions? ",
              strike: "*",
            })}
            {renderInputFeild({
              fieldName: "onHandDetails",
              label: "What is on hand and why (pcs and desc) ",
              strike: "*",
              tooltipText: "Confirm it matches the bill",
              tooltipKey: "onHandDetails",
            })}
            {renderRadioGroup(
              "didTerminalClearException",
              "Did the terminal clear the delivery exception (PR/RD/RF)?",
            )}

            {values?.didTerminalClearException === "N" &&
              renderNotes("Clear it in the 78/6")}
            {renderInputFeild({
              fieldName: "adeCallReason",
              label: "What does the ADE call for?",
              strike: "*",
              tooltipText: "Make sure the OH matches and read all the F18 notes",
              tooltipKey: "adeCallReason",
            })}
            {renderRadioGroup(
              "isF9NotFlashingInAde",
              "Is the F9 is not flashing in the ADE?",
            )}
            {values?.isF9NotFlashingInAde === "N" &&
              renderNotes("Email catkinson@saia.com.")}

            {renderRadioGroup(
              "hasBolInstructions",
              "Are there any instructions on the BOL or in (78/23)",
            )}

            {values?.hasBolInstructions === "Y" && (
              <>
                {renderRadioGroup(
                  "followedBolInstructions",
                  "Did they follow them 100%?",
                )}
                {values?.followedBolInstructions === "N" &&
                  renderNotes(
                    "Email the terminal and give detailed instructions",
                  )}
                {renderRadioGroup(
                  "doF18NotesMatchActions",
                  "Are the F18 notes showing what they did?",
                )}
                {values?.doF18NotesMatchActions === "N" &&
                  renderNotes("Have them update F18 notes")}

                {renderRadioGroup(
                  "hasImageCommunication",
                  "Is there communication in image?",
                  "hasImageCommunication",
                  "We have to show that we have followed the exact instructions provided by the shipper",
                )}
                {values?.hasImageCommunication === "N" &&
                  renderNotes("Have them image all the communication")}
              </>
            )}

            {renderCheckBox(
              "wasEveryDeliveryAttemptMade",
              "Confirm every attempt was made to get the freight delivery?",
            )}
            {renderInputFeild({ fieldName: "deliveryAttemptSteps" })}

            {renderCheckBox(
              "wasShipperContactedWithin24hrs",
              "Confirm the shipper was contacted for return authorization and value within 24 hrs of the ADE?",
            )}
            {renderInputFeild({ fieldName: "shipperContactSteps" })}

            {renderRadioGroup(
              "isFreightOnHandDueToDamage",
              "Is the freight On Hand due to damage? ",
            )}
            {values.isFreightOnHandDueToDamage === "Y" && (
              <>
                {renderInputFeild({
                  fieldName: "shipmentCallFor",
                  label: "Exactly what does the shipment call for?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "productDescription",
                  label: "What is the product?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "packageDamageDescription",
                  label: "What kind of pkg, is there damage to the pkg?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "actualProductDamageDetails",
                  label: "Is the actual product damaged, if so, how bad?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "actualProductNotDamagedDetails",
                  label: "Is any of the actual product NOT damaged, if so, how much?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "canProductBeRecoupedForRedelivery",
                  label: "Can the product be recouped for redelivery?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "isProductRepairable",
                  label: "Is this repairable?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "hasValidDamagePictures",
                  label: "Are there good pictures of the freight and the damage, in image?",
                  strike: "*",
                })}
                {renderInputFeild({
                  fieldName: "isProductGlass",
                  label: "Is the product glass?",
                  strike: "*",
                  tooltipText: "If glass is broken or shattered email catkinson@ saia.com",
                  tooltipKey: "glassIsBroken",
                })}
              </>
            )}

            {renderCheckBox(
              "ohnVerbiageCorrect",
              "Confirm the verbiage of the OHN is the correct verbiage with specific details of the NON damage?",
            )}
            <Grid item {...XL_GRID_PROPS} className="filter-margin">
              <h3 className="accordion-content-header">
                Confirm the OHN was sent to all parties?
              </h3>
            </Grid>

            {renderRadioGroup(
              "wasContactFrom7823",
              "Did the terminal get their contacts from the 78/23",
            )}
            {values?.wasContactFrom7823 === "Y" &&
              renderInputFeild({ fieldName: "stepsTakenFrom7823" })}

            {renderRadioGroup(
              "wasContactFrom8511",
              "Did the terminal get their contacts from the 85/11",
            )}

            {values?.wasContactFrom8511 === "Y" &&
              renderInputFeild({ fieldName: "stepsTakenFrom8511" })}

            {renderRadioGroup(
              "googleCheck",
              "Did we find more contacts from Google?",
            )}

            {values?.googleCheck === "Y" &&
              renderInputFeild({ fieldName: "googleCheckResponse" })}

            {renderRadioGroup(
              "pickupScreenCheck",
              "Did we find more contacts from the pickup screen?",
            )}
            {values?.pickupScreenCheck === "Y" &&
              renderInputFeild({ fieldName: "pickupScreenResponse" })}

            {renderRadioGroup(
              "salesRepCheck",
              "Did we get with the sales rep to get more contacts?",
            )}

            {values?.salesRepCheck === "Y" &&
              renderInputFeild({ fieldName: "salesRepResponse" })}

            {renderRadioGroup(
              "isShipperValueInWriting",
              "Is the value from the shipper, in writing",
              "isShipperValueInWriting",
              "If under $800, email catkinson@saia.com for disposition",
            )}
            {values?.isShipperValueInWriting === "N" &&
              renderNotes("E-mail the shipper for accurate value")}

            {renderCheckBox(
              "communicationImagedByTerminal",
              "Confirm all the communication with the shipper is imaged by the terminal?",
            )}

            {renderCheckBox(
              "f18NotesUpdatedByAll",
              "Confirm the F18 notes are updated from you and the terminal?",
            )}

            {renderRadioGroup(
              "freightHasAppointment",
              "Does the freight have an appointment.",
            )}

            {renderRadioGroup(
              "storageChargesApproved",
              "Have the storage charges been approved?",
              "storageChargesApproved",
              "All the contacts that you have found, including the contacts on the original OHN) with the specific verbiage and give a 72-hour deadline.  Make sure it clearly states the details of the damage product and non-damaged.  Do not use the word damage; describe the actual damage and product.",
            )}

            {["Y", "N"].includes(values?.storageChargesApproved) &&
              renderNotes(
                values?.storageChargesApproved === "Y"
                  ? "Image e-mail of approval"
                  : "Send the final email to all parties",
              )}
          </>

          {renderInputFeild({
            fieldName: "f18Note",
            label: "F18 Note",
            placeholder: "Enter F18 Note",
            strike: "*",
            disabled: formFieldsDisabled,
            rows: 2,
          })}
          {/* Action Buttons */}
          {!isCleared && actionButtons()}
        </Grid>
      </form>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(OnHandFollowup);
